import {
  getRedirectResult,
  getAuth,
  IdTokenResult,
  OAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  UserCredential,
} from "firebase/auth";
import { getApps, initializeApp } from "firebase/app";
import Bugsnag from "@bugsnag/js";
import { environmentName, firebaseConfig } from "src/config";
import { sha256 } from "src/utils/hash";

export enum EProvider {
  google = "google.com",
  apple = "apple.com",
}

export type AuthIntent = { type: "signIn" } | { type: "signUp"; kind?: string };

export type AuthCredentialResult = UserCredential & {
  nonce: string;
  oauthIdToken: string;
};

export type AuthResult =
  | AuthCredentialResult
  | { redirecting: true }
  | undefined;

export type RedirectAuthResult =
  | {
      status: "success";
      credential: AuthCredentialResult;
      providerId: EProvider;
      intent: AuthIntent;
    }
  | { status: "error" }
  | undefined;

export const initializeFirebase = () => initializeApp(firebaseConfig);

// The user dismissing the popup, or a request we cancelled ourselves, are
// ordinary interactions rather than faults worth reporting.
const benignAuthErrorCodes = [
  "auth/popup-closed-by-user",
  "auth/cancelled-popup-request",
  "auth/user-cancelled",
];

const redirectStorageKey = "firebase-auth-redirect";
const redirectAppName = "mobile-redirect-auth";

interface PendingRedirectAuth {
  providerId: EProvider;
  nonce: string;
  intent: AuthIntent;
}

let pendingAuth: {
  providerId: EProvider;
  promise: Promise<AuthResult>;
} | null = null;

// firebase throws an uncatchable internal assertion when a second popup cancels one still in flight
export const auth = (
  providerId: EProvider,
  intent: AuthIntent = { type: "signIn" },
) => {
  if (pendingAuth) {
    return pendingAuth.providerId === providerId
      ? pendingAuth.promise
      : Promise.resolve(undefined);
  }

  const promise = runAuth(providerId, intent).finally(() => {
    pendingAuth = null;
  });
  pendingAuth = { providerId, promise };

  return promise;
};

const runAuth = async (
  providerId: EProvider,
  intent: AuthIntent,
): Promise<AuthResult> => {
  try {
    // Mocking firebase response using playwright test is difficult to implement that is why we decided to add and environment variable to do this and be able to test SSO sign-in and sign-up with firebase.
    if (environmentName === "test") {
      return mockAuth();
    } else {
      const useRedirect = shouldUseRedirect();
      const auth = useRedirect ? getRedirectAuth(providerId) : getAuth();
      const unhashNonce = crypto.randomUUID();
      const hashNonce = await sha256(unhashNonce);

      const provider = new OAuthProvider(providerId);
      provider.setCustomParameters({ nonce: hashNonce });

      if (useRedirect) {
        if (!storePendingRedirect({ providerId, nonce: unhashNonce, intent })) {
          throw new Error("Unable to preserve Firebase redirect state");
        }
        await signInWithRedirect(auth, provider);
        return { redirecting: true };
      }

      const result = await signInWithPopup(auth, provider);

      return toAuthCredentialResult(result, unhashNonce);
    }
  } catch (error) {
    console.error("firebase auth error:", error);

    // Everything here used to stop at that console.error, which left a genuinely
    // broken sign-in — a misconfigured provider, a blocked popup, a rejected
    // credential — invisible in production.
    const code = (error as { code?: string })?.code;
    clearPendingRedirect();
    if (!code || !benignAuthErrorCodes.includes(code)) {
      Bugsnag.notify(error as Error, (event) => {
        event.addMetadata("firebaseAuth", { providerId, code });
      });
    }

    return undefined;
  }
};

export const isRedirecting = (
  result: AuthResult,
): result is { redirecting: true } => !!result && "redirecting" in result;

export const completeRedirectAuth = async (): Promise<RedirectAuthResult> => {
  const pendingRedirect = getPendingRedirect();
  if (!pendingRedirect) return undefined;

  try {
    const result = await getRedirectResult(
      getRedirectAuth(pendingRedirect.providerId),
    );
    if (!result) {
      throw new Error("Firebase redirect completed without a credential");
    }

    return {
      status: "success",
      credential: toAuthCredentialResult(result, pendingRedirect.nonce),
      providerId: pendingRedirect.providerId,
      intent: pendingRedirect.intent,
    };
  } catch (error) {
    const code = (error as { code?: string })?.code;
    if (!code || !benignAuthErrorCodes.includes(code)) {
      Bugsnag.notify(error as Error, (event) => {
        event.addMetadata("firebaseAuth", {
          providerId: pendingRedirect.providerId,
          code,
          operation: "redirect-result",
        });
      });
    }
    return { status: "error" };
  } finally {
    clearPendingRedirect();
  }
};

const toAuthCredentialResult = (
  credential: UserCredential,
  nonce: string,
): AuthCredentialResult => {
  const oauthCredential =
    typeof OAuthProvider.credentialFromResult === "function"
      ? OAuthProvider.credentialFromResult(credential)
      : null;

  return {
    ...credential,
    nonce,
    oauthIdToken: oauthCredential?.idToken || "",
  };
};

const shouldUseRedirect = () => {
  if (typeof navigator === "undefined") return false;

  return (
    /Android|iPad|iPhone|iPod/i.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
};

const getRedirectAuth = (providerId: EProvider) => {
  if (providerId !== EProvider.google) return getAuth();

  const redirectApp =
    getApps().find(({ name }) => name === redirectAppName) ||
    initializeApp(
      { ...firebaseConfig, authDomain: window.location.hostname },
      redirectAppName,
    );

  return getAuth(redirectApp);
};

const storePendingRedirect = (pendingRedirect: PendingRedirectAuth) => {
  try {
    window.sessionStorage.setItem(
      redirectStorageKey,
      JSON.stringify(pendingRedirect),
    );
    return true;
  } catch {
    // Without the original nonce, the API cannot validate the returned token.
    return false;
  }
};

const getPendingRedirect = (): PendingRedirectAuth | undefined => {
  try {
    const stored = window.sessionStorage.getItem(redirectStorageKey);
    if (!stored) return undefined;

    const parsed = JSON.parse(stored) as Partial<PendingRedirectAuth>;
    if (
      !Object.values(EProvider).includes(parsed.providerId as EProvider) ||
      typeof parsed.nonce !== "string" ||
      (parsed.intent?.type !== "signIn" && parsed.intent?.type !== "signUp")
    ) {
      clearPendingRedirect();
      return undefined;
    }

    return parsed as PendingRedirectAuth;
  } catch {
    clearPendingRedirect();
    return undefined;
  }
};

const clearPendingRedirect = () => {
  try {
    window.sessionStorage.removeItem(redirectStorageKey);
  } catch {
    // Storage can be disabled in privacy modes. There is nothing else to clear.
  }
};

// this is here because can't be move outside of src folder
export const mockAuth = (): AuthCredentialResult & {
  _tokenResponse: { oauthIdToken: string };
} => {
  return {
    user: {
      email: "test@contact.xyz",
      emailVerified: false,
      isAnonymous: false,
      metadata: { creationTime: "", lastSignInTime: "" },
      providerData: [],
      refreshToken: "",
      tenantId: null,
      delete: function (): Promise<void> {
        throw new Error("Function not implemented.");
      },
      getIdToken: function (): Promise<string> {
        return Promise.resolve("asdjgshjghj");
      },
      getIdTokenResult: function (): Promise<IdTokenResult> {
        throw new Error("Function not implemented.");
      },
      reload: function (): Promise<void> {
        throw new Error("Function not implemented.");
      },
      toJSON: function (): object {
        throw new Error("Function not implemented.");
      },
      displayName: null,
      phoneNumber: null,
      photoURL: null,
      providerId: "",
      uid: "",
    },
    providerId: "google.com",
    _tokenResponse: {
      oauthIdToken: "saghjsdaghsadghghj",
    },
    operationType: "signIn",
    nonce: "Asdas",
    oauthIdToken: "saghjsdaghsadghghj",
  };
};
